import React, { useEffect, useState } from 'react';
import { fetchClassifiedCategories, listServiceConfigurations } from '../../services/Service';
import { ServiceTypeConfig } from '../../types/Service';
import sendToast from '../../../utils/Toast';
import { useHistory } from 'react-router-dom';
import { useFacility } from '../../contexts/facility/FacilityContext';

interface CreateOrderStepOneProps {
    FacilityId: string;
    selectedServiceCategoryName: string;
    setSelectedServiceCategoryName: React.Dispatch<React.SetStateAction<string>>;
    setSelectedServiceCategoryHeading: React.Dispatch<React.SetStateAction<string>>;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
}

// Category picker for the order-status "Create Order" wizard modal — same data
// source/rules as ServiceOrderWidget/StepOne, but a category click only selects
// (highlighting the card) instead of auto-advancing, so the modal's shared
// Back/Next footer can drive navigation to match order-status-page.html.
const CreateOrderStepOne: React.FC<CreateOrderStepOneProps> = ({
    FacilityId, selectedServiceCategoryName, setSelectedServiceCategoryName, setSelectedServiceCategoryHeading, setIsFetching,
}) => {
    const history = useHistory();
    const [serviceCategories, setServiceCategories] = useState<ServiceTypeConfig[]>([]);
    const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});
    const { facilityDetails, loading: facilityLoading } = useFacility();

    useEffect(() => {
        const fetchServiceCategories = async () => {
            try {
                setIsFetching(true);
                const [allActiveCategories, iconMap] = await Promise.all([
                    fetchClassifiedCategories({ facilityId: FacilityId }),
                    listServiceConfigurations(FacilityId).catch((error) => {
                        sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching category icons');
                        return {} as Record<string, string>;
                    }),
                ]);
                setServiceCategories(allActiveCategories?.Result?.Content || []);
                setCategoryIcons(iconMap || {});
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching service categories');
            } finally {
                setIsFetching(false);
            }
        };
        fetchServiceCategories();
    }, [FacilityId]);

    const renderName = (category: ServiceTypeConfig) => {
        if (category.Name === "Menu" && !category.alternativeName) {
            return "Dining";
        }
        return category.alternativeName || category.Name;
    };

    const handleRedirectToMenu = () => {
        if (facilityLoading && !facilityDetails) {
            sendToast('warn', 'Still loading facility information. Please try again.');
            return;
        }
        if (facilityDetails?.featureConfigs?.configurations?.dining) {
            history.push({ pathname: `/menu` });
        } else {
            sendToast('warn', 'Dining feature is not enabled for this facility. Please contact support to enable it.');
        }
    };

    const handleServiceCategoryClick = (category: ServiceTypeConfig) => {
        if (category.Name === "Menu") {
            handleRedirectToMenu();
        } else {
            setSelectedServiceCategoryName(category.Name);
            setSelectedServiceCategoryHeading(renderName(category));
        }
    };

    return (
        <div className="dept-grid">
            {serviceCategories && serviceCategories.length > 0 ? (
                serviceCategories.map((category) => {
                    const iconClass = categoryIcons[category.Name] || categoryIcons.Other;
                    return (
                        <button
                            key={category.Name}
                            type="button"
                            className={`dept-card${selectedServiceCategoryName === category.Name ? ' selected' : ''}`}
                            onClick={() => handleServiceCategoryClick(category)}
                        >
                            <div className="dept-card-icon" aria-hidden="true">
                                {iconClass && <i className={iconClass} />}
                            </div>
                            <div className="dept-card-name">{renderName(category)}</div>
                        </button>
                    );
                })
            ) : (
                <span>No Service Categories Available</span>
            )}
        </div>
    );
};

export default CreateOrderStepOne;
